import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import pb from './lib/pocketbase';
import './ReferencesPage.css';
import './RecruitmentComponents.css'; // Pour réutiliser les styles du loader
import { useReferencesData } from './context/ReferencesContext';
import { useTheme } from './context/ThemeContext';
import { useSiteSettings } from './context/SiteSettingsContext';

const ReferencesLoader = () => {
  const [msgIdx, setMsgIdx] = useState(0);
  const messages = ["Exploration des projets...", "Sélection des meilleurs cas...", "Chargement de la galerie...", "Préparation du portfolio..."];

  useEffect(() => {
    const timer = setInterval(() => {
      setMsgIdx(prev => (prev + 1) % messages.length);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="recrutement-loader-container">
      <div className="loader-visual">
        <div className="loader-orbit orbit-1"></div>
        <div className="loader-orbit orbit-2"></div>
        <div className="loader-orbit orbit-3"></div>
        <div className="loader-logo-wrapper">
          <img src="/images/logo.png" alt="Version Net" className="loader-logo" />
        </div>
      </div>
      <div className="loader-message-wrapper">
        <p className="loader-text">{messages[msgIdx]}</p>
        <div className="loader-progress-bar">
          <div className="loader-progress-fill"></div>
        </div>
      </div>
    </div>
  );
};

function ReferencesPage() {
  const { references: allReferences, hero: heroFromContext, loading: dataLoading } = useReferencesData();
  const { theme } = useTheme();
  const { showContact } = useSiteSettings();

  // Filter only published items for public view
  const items = useMemo(() => allReferences.filter(r => r.published), [allReferences]);

  // Use context data or default
  const heroData = heroFromContext || {
    title: 'Références',
    subtitle: 'Découvrez une sélection de projets clients: contexte, enjeux, modules clés et environnement technique.',
    buttons: []
  };

  // UI state: catégorie active (all | infogerance | ingenierie | web)
  const [activeCategory, setActiveCategory] = useState('all');





  const categories = [
    { id: 'all', label: 'Tout voir' },
    { id: 'infogerance', label: 'Références Infogérance' },
    { id: 'ingenierie', label: 'Références Ingénierie' },
    { id: 'web', label: 'Références Web' }
  ];



  const filtered = useMemo(() => {
    let arr = items.slice();
    if (activeCategory !== 'all') {
      arr = arr.filter(r => {
        const rawCats = r.categories || r.categorie || [];
        const catsArray = Array.isArray(rawCats) ? rawCats : [rawCats];
        return catsArray.some(c => String(c).toLowerCase().trim() === activeCategory.toLowerCase());
      });
    }

    // Default sorting: annee desc then created desc
    return arr.sort((a, b) => {
      const ay = Number(a.annee) || 0;
      const by = Number(b.annee) || 0;
      if (by !== ay) return by - ay;
      const ac = new Date(a.created || 0).getTime();
      const bc = new Date(b.created || 0).getTime();
      return bc - ac;
    });
  }, [items, activeCategory]);

  const sorted = filtered; // Sorting is handled inside filtered memo now

  const logoUrl = (r) => (r?.logo ? pb.files.getURL(r, r.logo) : null);

  // State pour la taille d'affichage (small, medium, large)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('refs-view-mode') || 'medium');

  useEffect(() => {
    localStorage.setItem('refs-view-mode', viewMode);
  }, [viewMode]);

  return (
    <section id="references" className="section">
      <div className="section-inner">
        <nav className="breadcrumb" aria-label="Fil d'Ariane">
          <Link to="/">Accueil</Link>
          <span>/</span>
          <span>Références</span>
        </nav>
      </div>
      <div className="services-hero">
        <div className="services-hero-illus" aria-hidden="true" />
        <div className="services-hero-inner">
          <h1 className="services-hero-title"><span className="accent">{heroData.title}</span></h1>
          <p className="services-hero-subtitle">
            {heroData.subtitle}
          </p>
          <div
            className="services-cta"
            style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}
          >
            {heroData.buttons && heroData.buttons.length > 0 ? (
              heroData.buttons.map((btn, idx) => (
                <Link key={idx} className={`btn ${btn.style || 'btn-secondary'}`} to={btn.url}>
                  {btn.label}
                </Link>
              ))
            ) : (
              <>
                <Link className="btn btn-secondary" to="/services">Services</Link>
                <Link className="btn btn-secondary" to="/metiers">Métiers</Link>
                {showContact && <Link className="btn btn-primary" to="/contact">Nous contacter</Link>}
              </>
            )}
          </div>
        </div>
      </div>
      <div className="section-inner references-page">

        <div className="references-main">
          <div className="references-controls">
            <div className="refs-filters">
              <div className="refs-pills" role="list">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    className={`pill ${activeCategory === cat.id ? 'pill-active' : ''}`}
                    onClick={() => setActiveCategory(cat.id)}
                    role="listitem"
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="refs-right-controls" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
              <div className="refs-view-mode">
                <span className="refs-sort-label">Affichage:</span>
                <div className="segmented">
                  <button
                    type="button"
                    className={`seg ${viewMode === 'small' ? 'active' : ''}`}
                    onClick={() => setViewMode('small')}
                    title="Petites icônes"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
                  </button>
                  <button
                    type="button"
                    className={`seg ${viewMode === 'medium' ? 'active' : ''}`}
                    onClick={() => setViewMode('medium')}
                    title="Moyennes icônes"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"></rect><line x1="12" y1="3" x2="12" y2="21"></line></svg>
                  </button>
                  <button
                    type="button"
                    className={`seg ${viewMode === 'large' ? 'active' : ''}`}
                    onClick={() => setViewMode('large')}
                    title="Grandes icônes"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"></rect></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="refs-meta">
            <span>{sorted.length} résultat{sorted.length > 1 ? 's' : ''}</span>
          </div>

          {dataLoading ? (
            <ReferencesLoader />
          ) : (
            <div className={`references-grid view-${viewMode}`}>
              {sorted.length === 0 ? (
                <div className="card" style={{ gridColumn: '1 / -1', color: 'var(--text-muted)', textAlign: 'center' }}>Aucune référence publiée pour le moment.</div>
              ) : (
                sorted.map((r) => (
                  <Link key={r.id} to={`/references/${r.slug}`} className="ref-card">
                    <div className="ref-card-media">
                      {logoUrl(r) ? (
                        <img src={logoUrl(r)} alt={r.client || r.titre || 'Logo'} />
                      ) : (
                        <div className="ref-card-placeholder">{(r.client?.[0] || 'R').toUpperCase()}</div>
                      )}
                    </div>
                    <div className="ref-card-body">
                      <h3 className="ref-card-title">{r.titre || r.client}</h3>
                      {(viewMode === 'medium' || viewMode === 'large') && (
                        <div className="ref-card-meta">{[r.client, r.secteur, r.annee].filter(Boolean).join(' • ')}</div>
                      )}
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ReferencesPage;
